/**
 * Session « mode intervention » (God Mode) — SPEC §4.1, §4.3, A10.
 *
 * Un super admin entre dans un compte client SANS ses identifiants : le serveur
 * lui remet un jeton God Mode et l'identité cliente empruntée. Cette session vit
 * dans l'ONGLET, jamais dans la session personnelle du navigateur :
 *
 *   sessionStorage['bsr:godmode-token']    le jeton God Mode (claims id/companyId/gm)
 *   sessionStorage['bsr:godmode-identity'] la SURCOUCHE d'identité : un objet JSON
 *                                          { companyId, companyName, user, … } que
 *                                          `utils/localStorage` lit à la place du
 *                                          localStorage tant que l'onglet est en
 *                                          God Mode
 *
 * Conséquence voulue : la session client personnelle du super admin (localStorage)
 * n'est ni lue ni écrasée, et deux onglets peuvent tenir deux interventions.
 * Toute lecture brute de `localStorage` dans l'app contournerait la surcouche :
 * `rawStorage.lint.test.js` l'interdit.
 *
 * Ce module ne dépend d'AUCUN autre module de l'app (utils/localStorage l'importe,
 * donc la moitié des composants) et n'importe pas axios au chargement : l'en-tête
 * global `Authorization` est (ré)initialisé par `authContext` au rechargement
 * qui suit chaque entrée ou sortie, à partir du jeton que lit le helper de
 * stockage. Il ne touche jamais au jeton plateforme (`bsr:platform-token`), qui
 * permet de revenir au back-office.
 */

export const GOD_MODE_TOKEN_KEY = 'bsr:godmode-token';
export const GOD_MODE_IDENTITY_KEY = 'bsr:godmode-identity';

/**
 * Lu par la fiche d'un compte AVANT d'appeler le serveur : tant qu'il vaut false,
 * le bouton « Entrer dans le compte » est désactivé et aucune session God Mode
 * n'est ouverte côté serveur (le journal enregistrerait une intervention qui n'a
 * pas eu lieu).
 */
export const GOD_MODE_INSTALLABLE = true;

const API = process.env.REACT_APP_BACKEND_URL || '';

function lire(cle) {
  try {
    return sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}

export function getGodModeToken() {
  return lire(GOD_MODE_TOKEN_KEY) || null;
}

export function isGodMode() {
  return !!getGodModeToken();
}

function lireSurcouche() {
  try {
    const brut = lire(GOD_MODE_IDENTITY_KEY);
    if (!brut) return {};
    const objet = JSON.parse(brut);
    return objet && typeof objet === 'object' && !Array.isArray(objet) ? objet : {};
  } catch {
    return {};
  }
}

function ecrireSurcouche(objet) {
  try {
    if (!objet || Object.keys(objet).length === 0) sessionStorage.removeItem(GOD_MODE_IDENTITY_KEY);
    else sessionStorage.setItem(GOD_MODE_IDENTITY_KEY, JSON.stringify(objet));
  } catch {
    /* stockage indisponible : startGodMode le détecte à la relecture */
  }
}

export function overlayGet(cle) {
  const valeur = lireSurcouche()[cle];
  return valeur === undefined || valeur === null ? null : String(valeur);
}

/** Écrit une valeur (chaîne) dans la surcouche ; null/undefined la retire. */
export function overlaySet(cle, valeur) {
  const surcouche = lireSurcouche();
  if (valeur === undefined || valeur === null) delete surcouche[cle];
  else surcouche[cle] = String(valeur);
  ecrireSurcouche(surcouche);
}

export function overlayRemove(cle) {
  overlaySet(cle, null);
}

/** Vide la surcouche d'identité (PAS le jeton : `clearAll` en God Mode). */
export function overlayClear() {
  ecrireSurcouche({});
}

/**
 * Informations d'affichage de l'intervention en cours (bandeau) :
 * `{ sessionId, expiresAt, companyId, companyName, platformAdmin:{email,firstName,lastName} }`,
 * ou null hors God Mode. Tolère une surcouche abîmée.
 */
export function godModeInfo() {
  if (!isGodMode()) return null;
  let godMode = {};
  try {
    const user = JSON.parse(overlayGet('user') || 'null');
    godMode = (user && user.godMode) || {};
  } catch {
    godMode = {};
  }
  return {
    sessionId: godMode.sessionId || null,
    expiresAt: godMode.expiresAt || null,
    companyId: overlayGet('companyId'),
    companyName: godMode.companyName || overlayGet('companyName') || '',
    platformAdmin: godMode.platformAdmin || null,
  };
}

/** Où revenir en quittant : la fiche du compte, sinon la liste des comptes. */
export function godModeBackUrl() {
  const companyId = overlayGet('companyId');
  return companyId ? `/platform/companies/${companyId}` : '/platform/companies';
}

/**
 * Efface localement la session d'intervention (jeton + surcouche). Le
 * rechargement qui suit repart de la session personnelle, et `authContext`
 * réinitialise l'en-tête global à partir d'elle.
 */
export function abandonGodMode() {
  try {
    sessionStorage.removeItem(GOD_MODE_TOKEN_KEY);
    sessionStorage.removeItem(GOD_MODE_IDENTITY_KEY);
  } catch {
    /* rien à faire */
  }
}

/**
 * Installe la session God Mode à partir de la réponse de
 * `POST /platform/companies/:id/god-mode` ({ token, user }) puis charge
 * `/dashboard` (chargement complet, comme `installSession`). LÈVE une erreur au
 * message français affichable si l'installation est impossible.
 */
export function startGodMode(response) {
  const token = response && response.token;
  const user = response && response.user;
  if (!token || !user || !user.id || !user.companyId) {
    throw new Error("La réponse du serveur est incomplète : le mode intervention n'a pas pu s'ouvrir.");
  }

  const surcouche = {
    user: JSON.stringify(user),
    userId: user.id,
    companyId: user.companyId,
    companyName: user.companyName,
    companyLogo: user.companyImage,
    userFirstName: user.firstName,
    userLastName: user.lastName,
    userImage: user.image,
  };
  Object.keys(surcouche).forEach((cle) => {
    if (surcouche[cle] === undefined || surcouche[cle] === null) delete surcouche[cle];
    else surcouche[cle] = String(surcouche[cle]);
  });

  try {
    sessionStorage.setItem(GOD_MODE_TOKEN_KEY, token);
    sessionStorage.setItem(GOD_MODE_IDENTITY_KEY, JSON.stringify(surcouche));
  } catch {
    abandonGodMode();
    throw new Error("Le stockage de l'onglet est indisponible : le mode intervention ne peut pas s'ouvrir (navigation privée ?).");
  }
  if (getGodModeToken() !== token) {
    abandonGodMode();
    throw new Error("Le stockage de l'onglet est indisponible : le mode intervention ne peut pas s'ouvrir (navigation privée ?).");
  }

  // Chargement complet : authContext relit le jeton d'intervention et pose
  // l'en-tête global (comme `installSession` pour une connexion).
  window.location.assign('/dashboard');
}

/**
 * Clôt l'intervention : prévient le serveur (journal `godmode.ended`) avec le
 * jeton God Mode, vide la session de l'onglet, puis revient au back-office. Si
 * l'appel échoue (réseau, session déjà expirée), on quitte quand même : la
 * session serveur expire seule (2 h), et garder l'utilisateur prisonnier du
 * compte client serait pire.
 */
let sortieEnCours = false;

/**
 * Vrai dès que l'intervenant a demandé à sortir. Les requêtes encore en vol
 * échouent alors avec un jeton que le serveur vient de clore : l'intercepteur
 * ne doit y voir ni une expiration (« La session d'intervention a expiré. » au
 * retour d'une sortie volontaire) ni une session personnelle à effacer.
 */
export function isLeavingGodMode() {
  return sortieEnCours;
}

/**
 * Clôt côté serveur et efface localement la session d'intervention de l'onglet,
 * SANS naviguer. Ne lève jamais (la session serveur expire seule en cas d'échec).
 */
export async function closeGodModeSession() {
  const token = getGodModeToken();
  if (token) {
    try {
      // Import à l'appel : ce module est tiré par le helper de stockage, donc
      // par presque tous les composants, qui n'ont pas à charger axios avec lui.
      const { default: axios } = await import('axios');
      await axios.post(`${API}/api/v2/platform/god-mode/end`, null, {
        headers: { Authorization: `Bearer ${token}` },
        // L'intercepteur global ne doit pas traiter l'échec de CET appel
        // (session expirée) comme une expiration en cours de navigation.
        skipSessionHandling: true,
      });
    } catch {
      /* on quitte quand même */
    }
  }
  abandonGodMode();
  try {
    const { default: axios } = await import('axios');
    delete axios.defaults.headers.common.Authorization;
  } catch {
    /* le rechargement qui suit le réinitialise de toute façon */
  }
}

export async function endGodMode() {
  sortieEnCours = true;
  const retour = godModeBackUrl();
  await closeGodModeSession();
  window.location.assign(retour);
}
